<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('monthlySaleItem.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('monthlySaleItem.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <div class="row">
      <div class="col-md-3">
        <div class="form-group">
          <ts-card>
            <ts-card-wrapper>
              <ts-loading-banner :loading="loading">
                <form>
                  <fieldset>
                    <legend>{{ $t('monthlySaleItem.criteria') }}</legend>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('monthlySaleItem.branchName')
                          }}</label>
                          <a-select
                            v-model="model.branch_id"
                            :options="branches"
                            class="ts-w-full"
                          ></a-select>
                          <div class="validate" v-if="errors.has('branch_id')">
                            {{ errors.first('branch_id') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="form-group">
                          <label class="required">{{
                            $t('monthlySaleItem.saleDate')
                          }}</label>
                          <a-range-picker
                            v-model="model.date_range"
                            :ranges="{
                              Today: [moment(), moment()],
                              'This Month': [
                                moment().startOf('month'),
                                moment().endOf('month')
                              ]
                            }"
                            format="DD-MM-YYYY"
                          />
                          <div class="validate" v-if="errors.has('date_range')">
                            {{ errors.first('date_range') }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-12">
                        <label>{{
                          $t('studentEnrollmentReport.reportType')
                        }}</label>
                        <div class="form-group">
                          <div class="radio radio-css radio-inline">
                            <input
                              v-model="model.report_type"
                              type="radio"
                              id="summary"
                              :value="'summary'"
                            />
                            <label for="summary">
                              {{ $t('report.summary') }}
                            </label>
                          </div>
                          <div class="radio radio-css radio-inline">
                            <input
                              v-model="model.report_type"
                              type="radio"
                              id="detail"
                              :value="'details'"
                            />
                            <label for="detail"
                              >{{ $t('report.detail') }}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          outline
                          color="success"
                          class="btn-block"
                          @click.prevent="exportExcel"
                          :waiting="exporting"
                          ><i class="far fa-file-excel"></i>
                          {{ $t('export') }}</ts-button
                        >
                      </div>
                      <div class="col-md-6 ts-text-right">
                        <ts-button
                          color="danger"
                          outline
                          class="btn-block"
                          @click.prevent="preview"
                          :waiting="waiting"
                          ><i class="far fa-file-pdf"></i>
                          {{ $t('preview') }}</ts-button
                        >
                      </div>
                    </div>
                  </fieldset>
                </form>
              </ts-loading-banner>
            </ts-card-wrapper>
          </ts-card>
        </div>
      </div>
      <div class="col-md-9">
        <ts-card>
          <ts-card-wrapper>
            <form>
              <fieldset>
                <legend>{{ $t('monthlySaleItem.preview') }}</legend>
                <ts-preview-pdf v-model="waiting" :src="src" />
              </fieldset>
            </form>
          </ts-card-wrapper>
        </ts-card>
      </div>
    </div>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions } from 'vuex'
import * as FileDownload from 'downloadjs'
import { trim } from 'lodash'

export default {
  name: 'monthlySaleItem',
  data () {
    return {
      waiting: false,
      loading: false,
      exporting: false,
      errors: new Errors(),
      branches: [],
      src: '',
      model: {
        date_range: [moment(), moment()],
        branch_id: null,
        report_type: 'summary'
      }
    }
  },
  created () {
    this.fetchData()
    this.model.branch_id = this.$store.state.authUser.default_branch_id
  },
  methods: {
    ...mapActions('reportInventory', ['monthlySaleItem', 'getBranch']),
    moment,
    async fetchData () {
      this.loading = true
      await this.getBranch()
        .then(response => {
          this.branches = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.loading = false
        })
    },
    preview () {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.monthlySaleItem(
        Object.assign({}, this.model, {
          date_range: [
            this.model.date_range[0].format('DD-MM-YYYY'),
            this.model.date_range[1].format('DD-MM-YYYY')
          ]
        })
      )
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel () {
      this.exporting = true
      this.errors = new Errors()

      this.$store
        .dispatch(
          'reportInventory/monthlySaleItemExcel',
          Object.assign({}, this.model, {
            date_range: [
              this.model.date_range[0].format('DD-MM-YYYY'),
              this.model.date_range[1].format('DD-MM-YYYY')
            ]
          })
        )
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    }
  }
}
</script>
